<!-- /////////////////////////////////////////////////////////////////////////// TEMPLATE -->

<template>
    <div class="lsn-table__line" :class="{ 'lsn-table__line--open': showDetails }">
        <div
            class="lsn-table__line-summary group"
            :class="{ 'lsn-table__line-summary--not-openable': openable && !lineOpenable }"
            @click="toggleOpen"
        >
            <!-- Checkbox (if selectable) -->
            <lsn-table-cell v-if="selectable" class="lsn-table__checkbox">
                <mdi-icon
                    :icon="selected ? $const.icons.mdiCheckboxMarkedOutline : $const.icons.mdiCheckboxBlankOutline"
                    class="lsn-table__checkbox-input"
                    :class="{ 'lsn-table__checkbox-input--checked': selected }"
                    @click.stop="$emit(selected ? 'unselected' : 'selected', item.id)"
                />
            </lsn-table-cell>

            <!-- Chevron (if openable) -->
            <lsn-table-cell v-if="openable" class="lsn-table__chevron">
                <mdi-icon v-if="lineOpenable" icon="mdiChevronRight" />
            </lsn-table-cell>

            <!-- Line Content -->
            <slot name="summary" />
        </div>

        <div v-if="showDetails" class="lsn-table__line-details">
            <slot name="details" />
        </div>
    </div>
</template>


<!-- /////////////////////////////////////////////////////////////////////////// SCRIPT -->

<script>
import LsnTableCell from './LsnTableCell.vue';

export default
{
    components:
    {
        LsnTableCell,
    },

    props:
    {
        item:
        {
            type:     Object,
            required: true,
        },

        selectable:
        {
            type:    Boolean,
            default: false,
        },

        openable:
        {
            type:    Boolean,
            default: false,
        },

        lineOpenable:
        {
            type:    Boolean,
            default: true,
        },

        selected:
        {
            type:    Boolean,
            default: false,
        },
    },

    emits: ['selected', 'unselected'],

    data()
    {
        return {
            showDetails: false,
        };
    },

    methods:
    {
        toggleOpen()
        {
            if(this.openable && this.lineOpenable)
            {
                this.showDetails = !this.showDetails;
            }
        },
    },
};
</script>


<!-- /////////////////////////////////////////////////////////////////////////// STYLE -->

<style>
@reference "#main.css";

.lsn-table__line
{
    @apply border-b border-primary-100;
}

.lsn-table__line--open
{
    @apply bg-gray-50;
}

.lsn-table__checkbox
{
    @apply flex justify-center items-center;
}

.lsn-table__checkbox-input
{
    @apply cursor-pointer inline-block -mt-px w-5 h-5 text-gray-500;
}

.lsn-table__checkbox-input--checked
{
    @apply text-primary-500;
}

.lsn-table__chevron > .mdi-icon
{
    margin-top: -2px;
    @apply inline-block align-middle w-4 h-4 rounded-full border border-primary-500 text-primary-300 transform;
}

.lsn-table__line--open > .lsn-table__line-summary > .lsn-table__chevron > .mdi-icon
{
    @apply rotate-90;
}


/* ------------------------------ Line Summary */

.lsn-table--openable > .lsn-table__body > .lsn-table__line > .lsn-table__line-summary
{
    @apply cursor-pointer;

    &.lsn-table__line-summary--not-openable
    {
        @apply cursor-default;
    }
}

/* Default line layout: */
/*   - One fixed-width column for selection checkbox */
/*   - One fixed-width column for open/close toggle button */
/*   - All other columns distributed equally */
.lsn-table__line-summary
{
    @apply py-2 items-center
        hover:bg-primary-50;

    display: grid;
    grid-template-columns: var(--checkbox-width) var(--open-toggle-width) repeat(auto-fit, minmax(0, 1fr));
    grid-template-rows: auto;
}

/* If the table isn't selectable, there's no checkbox, so remove that fixed-width column. */
.lsn-table:not(.lsn-table--selectable) > .lsn-table__body > .lsn-table__line > .lsn-table__line-summary
{
    grid-template-columns: var(--open-toggle-width) repeat(auto-fit, minmax(0, 1fr));
}

/* If the table isn't openable, there's no chevron, so remove that fixed-width column. */
.lsn-table:not(.lsn-table--openable) > .lsn-table__body > .lsn-table__line > .lsn-table__line-summary
{
    grid-template-columns: var(--checkbox-width) repeat(auto-fit, minmax(0, 1fr));
}

/* If the table isn't neither selectable nor openable, there's no checkbox, so remove that fixed-width column. */
.lsn-table:not(.lsn-table--selectable):not(.lsn-table--openable) > .lsn-table__body > .lsn-table__line > .lsn-table__line-summary
{
    grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
}

.lsn-table__chevron
{
    @apply text-center;
}

.lsn-table a
{
    @apply text-primary-300 transition hover:text-primary-800;
}

.lsn-table .lsn-table__link--disabled
{
    @apply cursor-not-allowed text-gray-400 hover:text-gray-400;
}
</style>
